import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { CheckIcon } from "../../../../Icon";
import { useReportContext } from "../../../ReportContext/useReportContext";
import "./TimeLine.scss";

const TimelineItem = ({
  title,
  color,
  navigatedStep,
  currentStep,
  isFuture,
  onClick,
  isTop,
}) => {
  const theme = useTheme();
  // Container
  const stepContainer = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "45px",
    flex: 1,
    position: "relative",
    cursor: isFuture ? "not-allowed" : "pointer",
    opacity: isFuture ? 0.3 : 1,
    transition: "opacity 0.5s",
    zIndex: 10,
  };
  // Texts and sphere
  const element = {
    scale: navigatedStep ? "1.25" : "1",
    transition: "all 0.4s",
  };
  // Sphere representing a step in the timeline
  const sphereStyle = {
    width: "20px",
    height: "20px",
    backgroundColor: !isFuture ? color : theme.palette.blueGray700.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "100%",
    position: "relative",
    zIndex: 20,
    boxShadow:
      navigatedStep &&
      `0 0 0 3px ${theme.palette.common.white},
      0 0 4px 5px ${theme.palette.blueGray200.main}`,
  };
  // Represents the current step in which the report is located
  const triangleStyle = {
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    [isTop ? "bottom" : "top"]: "calc(100% + 2px)",
    [isTop
      ? "borderTop"
      : "borderBottom"]: `8px solid ${theme.palette.limePunch.main}`,
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 20,
  };

  return (
    <div style={stepContainer} onClick={() => !isFuture && onClick()}>
      <div style={element}>
        <div style={sphereStyle}>
          {!isFuture && !currentStep && (
            <CheckIcon color={theme.palette.common.white} />
          )}
        </div>
      </div>
      {currentStep && <div style={triangleStyle}></div>}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: { xs: "0.65rem", sm: "0.8rem" },
          position: "absolute",
          left: 0,
          right: 0,
          textAlign: "center",
          bottom: isTop && { xs: -25, sm: -15 },
          top: !isTop && { xs: -15, sm: -18 },
          lineHeight: 1.35,
        }}
      >
        {title}
      </Typography>
    </div>
  );
};

export const TimeLine = ({ reportDetail, interstateNavigation }) => {
  const theme = useTheme();
  const [t] = useTranslation("report");
  const { status_locales } = useReportContext();
  // Step in the investigation process
  const [currentStep, setCurrentStep] = useState("");
  // Current step in which the user is placed
  const [navigatedStep, setNavigatedStep] = useState(1);
  //

  const returnColor = (status, color) => {
    if (!reportDetail?.status?.details && status === "closed") {
      return status_locales["closing_process"].color;
    }
    return color;
  }

  // Base line styles
  const timelineData = [
    {
      id: 1,
      status: "new",
      color: theme.palette.blue.main,
    },
    {
      id: 2,
      status: "pending",
      color: theme.palette.purple.main,
    },
    {
      id: 3,
      status: "process",
      color: theme.palette.green.main,
    },
    {
      id: 4,
      status: "finish",
      color: theme.palette.yellow.main,
    },
    {
      id: 5,
      status: "closed",
      color: theme.palette.red.main,
    },
  ];

  // Navigate beetween statuses
  const navigateStatuses = (status, id) => {
    const prevState = timelineData.findIndex(
      (state) => state.status === reportDetail?.status?.current
    );

    interstateNavigation(
      status,
      prevState ? timelineData[prevState - 1]?.status : false
    );

    if (id <= currentStep) {
      setNavigatedStep(id);
    }
  };
  // Determines current report status
  useEffect(() => {
    const statusActive = timelineData.findIndex(
      (state) => state.status == reportDetail?.status?.current
    );
    setCurrentStep(statusActive ? statusActive + 1 : 1);
    setNavigatedStep(statusActive ? statusActive + 1 : 1);
  }, [reportDetail?.status?.current]);

  const lineStyle = {
    position: "absolute",
    top: "50%",
    left: 0,
    width: "100%",
    height: "2px",
    background: `linear-gradient(
                  to right, 
                  ${theme.palette.common.white} 0%,
                  ${
                    currentStep >= 1
                      ? theme.palette.blue.main
                      : alpha(theme.palette.blueGray50.main, 0.4)
                  } 10%,
                  ${
                    currentStep >= 2
                      ? theme.palette.purple.main
                      : alpha(theme.palette.blueGray50.main, 0.4)
                  } 10%,
                  ${
                    currentStep >= 3
                      ? theme.palette.green.main
                      : alpha(theme.palette.blueGray50.main, 0.4)
                  } ${currentStep < 3 ? 32 : 50}%,
                  ${
                    currentStep >= 4
                      ? theme.palette.yellow.main
                      : alpha(theme.palette.blueGray50.main, 0.4)
                  } 50%,
                  ${
                    currentStep >= 5
                      ? theme.palette.red.main
                      : alpha(theme.palette.blueGray50.main, 0.4)
                  } ${currentStep < 5 ? 90 : 72}%,
                  ${theme.palette.common.white} 100%
                )`,
    transform: "translateY(-50%)",
    zIndex: 1,
  };

  return (
    <Box sx={{ width: "100%", mt: 1.5 }} className="timeLine">
      <Stack
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexDirection="row"
        flexWrap="nowrap"
        position="relative"
        zIndex={0}
      >
        <div style={lineStyle}></div>
        {timelineData.map((item, index) => (
          <TimelineItem
            key={item.id}
            title={t(
              "status." +
                (reportDetail?.status?.details === undefined &&
                item.status === "closed"
                  ? "closing_process"
                  : item.status)
            )}
            color={returnColor(item.status, item.color)}
            navigatedStep={item.id === navigatedStep}
            currentStep={item.id === currentStep}
            isFuture={item.id > currentStep}
            isTop={index % 2 === 0}
            onClick={() => navigateStatuses(item.status, item.id)}
          />
        ))}
      </Stack>
    </Box>
  );
};
